<!-- FileWizardAntd.vue -->
<template>
  <div class="fixed inset-0 w-screen h-screen bg-gray-100 flex flex-col z-50">
    <!-- Spinner overlay -->
    <div v-if="isLoading" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <a-spin size="large">
        <div class="bg-white p-6 rounded-lg shadow-lg">
          <p class="mt-2 text-gray-700">Por favor, espere...</p>
        </div>
      </a-spin>
    </div>

    <!-- Header con título y botón de cierre -->
    <div class="bg-white shadow-md p-2 flex items-center justify-between">
      <h2 class="text-lg font-semibold">Carga de Pasajeros</h2>
      <a-icon type="close" @click="$emit('onClose')" class="modal-close-icon" />
    </div>

    <!-- Contenedor principal -->
    <div class="flex flex-col flex-1 overflow-hidden">
      <!-- Steps siempre visible en la parte superior -->
      <div class="p-2 bg-white shadow-md mb-2">
        <a-steps :current="current">
          <a-step title="Cargar" />
          <a-step title="Revisar Datos" />
          <a-step title="Confirmar" />
        </a-steps>
      </div>

      <div class="flex-1 px-6 overflow-y-auto">
        <!-- Step 1: Carga de archivo -->
        <div v-if="current === 0" class="mx-auto bg-white p-4 rounded-md shadow-md">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <!-- Columna izquierda: Upload de archivos -->
            <div class="w-full">
              <LmUploadFiles
                class="w-full"
                label="Podés subir un archivo con una lista de pasajeros"
                duplicateCheck
                v-model="selectedFile"
                :dropzoneOptions="{
                  acceptedFiles: acceptedFiles,
                  headers: {
                    'Cache-Control': null,
                    'X-Requested-With': null
                  },
                  withCredentials: true,
                  thumbnailWidth: 150,
                  maxFilesize: 20,
                  timeout: 120000,
                  addRemoveLinks: true,
                  maxFiles: 1,
                  clickable: true
                }"
              />
            </div>

            <!-- Columna derecha: Editor de texto -->
            <div class="w-full">
              <div>O pegar el texto que quieras importar con IA</div>
              <textarea
                v-model="plainText"
                class="w-full h-44 p-3 mt-3 border-2 border-dashed border-gray-300 rounded-md focus:outline-none focus:border-blue-500 resize-none hover:border-blue-500 transition-colors"
                placeholder="Pegá aquí tu lista de pasajeros en cualquier formato (texto plano, tabla, etc.)"
              ></textarea>
            </div>
          </div>
        </div>

        <!-- Step 2: Revisión de datos -->
        <div v-if="current === 1" class="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-4 bg-white p-2 rounded-md shadow-md">
          <div class="w-full md:w-1/3">
            <a-card class="border-0 bg-transparent" title="Contenido del archivo procesado">
              <!-- Bloque de información procesada a la izquierda con scroll independiente -->
              <div class="p-4 bg-gray-100 rounded-md overflow-y-auto" style="max-height: calc(100vh - 300px);">
                <p v-html="highlightedText"></p>
              </div>
            </a-card>
          </div>

          <!-- Tabla de pasajeros a la derecha con scroll independiente -->
          <div class="w-full md:w-2/3">
            <a-card class="border-0 bg-transparent" title="Pasajeros encontrados">
              <a-button :disabled="isLoading" type="primary" @click="addPassenger" class="mb-4">
                <a-icon type="user-add" /> Agregar Pasajero
              </a-button>
              <!-- <a-button class="ml-1" :disabled="isLoading" @click="invertNames">
                <a-icon type="swap" /> Invertir
              </a-button> -->
              <a-table
                :columns="columns"
                :data-source="passengers"
                :pagination="false"
                size="small"
                :scroll="{ y: 'calc(100vh - 400px)' }"
                :rowKey="(record, index) => index"
              >
                <template slot="index" slot-scope="text, record, index">
                  {{ index + 1 }}
                </template>
                <template slot="firstname" slot-scope="text, record">
                  <a-input
                    v-model="record.firstname"
                    @change="handleEdit(record, index)"
                  />
                </template>
                <template slot="lastname" slot-scope="text, record">
                  <a-input
                    v-model="record.lastname"
                    @change="handleEdit(record, index)"
                  />
                </template>
                <template slot="birthdate" slot-scope="text, record">
                  <a-input
                    v-model="record.birthdate"
                    @change="handleEdit(record, index)"
                  />
                </template>
                <template slot="passport" slot-scope="text, record">
                  <a-input
                    v-model="record.passport"
                    @change="handleEdit(record, index)"
                  />
                </template>
                <template slot="action" slot-scope="text, record, index">
                  <a-button :disabled="isLoading" type="link" @click="insertPassengerAfter(index)">
                    <a-icon type="plus-circle" style="color: #52c41a;" />
                  </a-button>
                  <a-button :disabled="isLoading" type="link" @click="removePassenger(index)">
                    <a-icon type="delete" style="color: #ff4d4f;" />
                  </a-button>
                </template>
                <template #footer>
                  <strong>Total de pasajeros: {{ passengers.length }}</strong>
                </template>
              </a-table>
            </a-card>
          </div>
        </div>

        <!-- Step 3: Confirmación -->
        <div v-if="current === 2" class="mx-auto bg-white p-6 rounded-md shadow-md">
          <a-card class="border-0 bg-transparent" title="Resumen de Pasajeros">
            <a-table
              :columns="columns"
              :data-source="passengers"
              :pagination="false"
              size="small"
              :scroll="{ y: 'calc(100vh - 400px)' }"
              :rowKey="record => record.firstname + record.lastname"
            >
              <template slot="index" slot-scope="text, record, index">
                {{ index + 1 }}
              </template>
              <template slot="action" slot-scope="text, record, index">
                <a-button :disabled="isLoading" type="link" @click="removePassenger(index)">
                  <a-icon type="delete" style="color: #ff4d4f;" />
                </a-button>
              </template>
              <template #footer>
                <strong>Total de pasajeros: {{ passengers.length }}</strong>
              </template>
            </a-table>
          </a-card>
        </div>
      </div>

      <!-- Footer con botones de navegación -->
      <div class="bg-white shadow-md p-2 flex justify-end space-x-2">
        <a-button v-if="current > 0" :disabled="isLoading" @click="prev">Anterior</a-button>
        <a-button
          v-if="current === 0"
          type="primary"
          @click="extractPassengers"
          :disabled="!canProceed || isLoading"
        >
          Analizar con IA
        </a-button>
        <a-button
          v-if="current === 1"
          type="primary"
          @click="next"
          :disabled="!canProceed || isLoading"
        >
          Siguiente
        </a-button>
        <a-button
          v-if="current === 2"
          :disabled="isLoading"
          type="primary"
          @click="onSubmitConfirmation"
        >
          Finalizar
        </a-button>
      </div>
    </div>
  </div>
</template>

<script>
// Components
import LmUploadFiles from '@/components/form/elements/LmUploadFiles'

// Mixins
import lmToasts from '@/mixins/lmToasts'

// Utils
import commonsApi from '@/api/commons'

export default {
  name: 'LmPassengerWizard',
  mixins: [lmToasts],
  components: {
    LmUploadFiles
  },
  props: {
    creatorServiceEndpoint: {
      type: String,
      required: true
    }
  },
  data () {
    return {
      current: 0,
      isLoading: false,
      selectedFile: null,
      passengers: [],
      acceptedFiles: [{ name: 'doc' }, { name: 'docx' }, { name: 'xlsx' }],
      extractedText: null,
      highlightedText: null,
      plainText: null,
      columns: [
        {
          title: '#',
          dataIndex: 'index',
          key: 'index',
          width: '40px',
          scopedSlots: { customRender: 'index' }
        },
        {
          title: 'Apellido',
          dataIndex: 'lastname',
          key: 'lastname',
          scopedSlots: { customRender: 'lastname' }
        },
        {
          title: 'Nombre',
          dataIndex: 'firstname',
          key: 'firstname',
          scopedSlots: { customRender: 'firstname' }
        },
        {
          title: 'F. Nacimiento',
          dataIndex: 'birthdate',
          key: 'birthdate',
          scopedSlots: { customRender: 'birthdate' }
        },
        {
          title: 'Pasaporte',
          dataIndex: 'passport',
          key: 'passport',
          scopedSlots: { customRender: 'passport' }
        },
        {
          title: 'Acción',
          key: 'action',
          width: '100px',
          scopedSlots: { customRender: 'action' }
        }
      ]
    }
  },
  watch: {
    plainText (newVal) {
      this.plainText = newVal.trim()
    }
  },
  computed: {
    canProceed () {
      switch (this.current) {
        case 0:
          return (this.selectedFile || this.plainText) && !this.isLoading
        case 1:
          return !this.isLoading && this.passengers.length > 0
        default:
          return true
      }
    }
  },
  methods: {
    invertNames () {
      this.passengers.forEach(passenger => {
        const temp = passenger.firstname
        passenger.firstname = passenger.lastname
        passenger.lastname = temp
      })
      this.highlightExtractedText()
    },
    addPassenger () {
      this.passengers.push({ firstname: '', lastname: '' })
    },
    insertPassengerAfter (index) {
      this.passengers.splice(index + 1, 0, { firstname: '', lastname: '' })
    },
    removePassenger (index) {
      this.passengers.splice(index, 1)
      this.highlightExtractedText()
    },
    handleEdit (record, index) {
      this.$set(this.passengers, index, record)
      this.highlightExtractedText()
    },
    highlightExtractedText () {
      if (!this.extractedText) return ''

      // Primero, escapamos los caracteres especiales de HTML
      let htmlText = this.extractedText.replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;')

      // Luego, reemplazamos los saltos de línea por <br>
      htmlText = htmlText.replace(/\n/g, '<br>')

      // Creamos un mapa de reemplazos
      const replacements = new Map()

      // Ahora, resaltamos las ocurrencias de fullname, firstname y lastname
      this.passengers.forEach(entity => {
        if (entity.firstname) {
          replacements.set(entity.firstname, `<span style="background-color: #FFFF99;">${entity.firstname}</span>`)
        }
        if (entity.lastname) {
          replacements.set(entity.lastname, `<span style="background-color: #FFFF99;">${entity.lastname}</span>`)
        }
      })

      // Ordenamos las claves del mapa por longitud, de mayor a menor
      const sortedKeys = Array.from(replacements.keys()).sort((a, b) => b.length - a.length)

      // Realizamos los reemplazos
      sortedKeys.forEach(key => {
        // eslint-disable-next-line no-useless-escape
        const regex = new RegExp(key.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&'), 'gi')
        htmlText = htmlText.replace(regex, replacements.get(key))
      })

      this.highlightedText = htmlText
    },

    async extractPassengers () {
      try {
        this.isLoading = true
        const res = await commonsApi.axiosPostWrapper('/ia/extract_passengers', {
          file: this.selectedFile,
          plainText: this.plainText
        })
        this.passengers = res.data.items
        this.extractedText = res.data?.extractedText
        this.highlightExtractedText() // Llamar a la función de resaltado inicialmente
      } catch (error) {
        console.log('Error al extraer pasajeros:', error)
      } finally {
        this.isLoading = false
        if (this.passengers?.length > 0) {
          this.current = 1
        } else {
          this.lmModalWarningMessage({
            resultTitle: 'Nuestros servidores de IA están ocupados',
            resultSubtitle: 'Por favor intentá nuevamente en unos segundos',
            btnText: 'Aceptar'
          })
        }
      }
    },
    validatePassengers () {
      return this.passengers.every(passenger =>
        passenger.firstname.trim() !== '' && passenger.lastname.trim() !== ''
      )
    },
    next () {
      if (this.current === 1) { // Si estamos en el paso de "Revisar Datos"
        if (!this.validatePassengers()) {
          this.lmToastWarningMessage('Todos los pasajeros deben tener nombre y apellido', 'Error')
          return
        }
      }
      this.current++
    },
    prev () {
      this.current--
    },
    onSubmitConfirmation () {
      this.lmModalInformationMessage({
        modalClosable: false,
        modalKeyboard: false,
        resultTitle: 'Confirmación de creación de pasajeros',
        resultSubtitle: `¿Está seguro de que desea crear automáticamente ${this.passengers.length} pasajeros?`,
        buttons: [
          {
            btnText: this.$t('cancel'),
            btnType: 'danger',
            btnGhost: true,
            btnCallback: () => {
            }
          },
          {
            btnText: this.$t('accept'),
            btnCallback: () => {
              this.handleSubmit()
            }
          }
        ]
      })
    },
    async handleSubmit () {
      try {
        this.isLoading = true
        await commonsApi.axiosPostWrapper(`${this.creatorServiceEndpoint}/bulk`, {
          passengers: this.passengers,
          programId: this.$route.params.id
        })
        this.$emit('onPassengersCreated', this.passengers)
      } catch (error) {
        console.error('Error saving passengers:', error)
      } finally {
        this.isLoading = false
      }
    }
  }
}
</script>
